import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  template_type: z.string().min(1, 'Template type is required'),
  is_active: z.boolean().default(true),
  sort_order: z.number().min(0).default(0),
});

type FormData = z.infer<typeof formSchema>;

interface Section {
  title: string;
  description: string;
  fields: Field[];
}

interface Field {
  name: string;
  type: string;
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
}

const AdminNotesTemplateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdit = Boolean(id);

  const [sections, setSections] = useState<Section[]>([]);
  const [aiPrompts, setAiPrompts] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      template_type: 'custom',
      is_active: true,
      sort_order: 0,
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      fetchTemplate();
    }
  }, [id, isEdit]);

  const fetchTemplate = async () => {
    if (!id) return;
    
    try {
      const { data, error } = await supabase
        .from('notes_templates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      form.reset({
        name: data.name,
        template_type: data.template_type,
        is_active: data.is_active,
        sort_order: data.sort_order,
      });

      setSections((data.content_structure as any)?.sections || []);
      setAiPrompts((data.ai_prompts as any) || {});
    } catch (error) {
      console.error('Error fetching template:', error);
      toast({
        title: "Error",
        description: "Failed to load template",
        variant: "destructive",
      });
    }
  };

  const handleAddSection = () => {
    setSections([...sections, {
      title: '',
      description: '',
      fields: []
    }]);
  };

  const handleUpdateSection = (index: number, section: Section) => {
    const newSections = [...sections];
    newSections[index] = section;
    setSections(newSections);
  };

  const handleDeleteSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const handleAddField = (sectionIndex: number) => {
    const newSections = [...sections];
    newSections[sectionIndex].fields.push({
      name: '',
      type: 'text',
      label: '',
      required: false,
      placeholder: ''
    });
    setSections(newSections);
  };

  const handleUpdateField = (sectionIndex: number, fieldIndex: number, field: Field) => {
    const newSections = [...sections];
    newSections[sectionIndex].fields[fieldIndex] = field;
    setSections(newSections);
  };

  const handleDeleteField = (sectionIndex: number, fieldIndex: number) => {
    const newSections = [...sections];
    newSections[sectionIndex].fields = newSections[sectionIndex].fields.filter((_, i) => i !== fieldIndex);
    setSections(newSections);
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const templateData = {
        name: data.name,
        template_type: data.template_type,
        is_active: data.is_active,
        sort_order: data.sort_order,
        content_structure: { sections } as any,
        ai_prompts: aiPrompts as any,
      };

      if (isEdit && id) {
        const { error } = await supabase
          .from('notes_templates')
          .update(templateData)
          .eq('id', id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('notes_templates')
          .insert(templateData);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Template ${isEdit ? 'updated' : 'created'} successfully`,
      });

      navigate('/admin/cms/notes-templates');
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: "Error",
        description: `Failed to ${isEdit ? 'update' : 'create'} template`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/cms/notes-templates')}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEdit ? 'Edit Template' : 'New Template'}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? 'Modify template settings and structure' : 'Create a new notes template'}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Configure the basic template settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter template name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="template_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select template type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="soap">SOAP</SelectItem>
                        <SelectItem value="progress_note">Progress Note</SelectItem>
                        <SelectItem value="assessment">Assessment</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center space-x-4">
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormLabel>Active</FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sort_order"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sort Order</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value || 0}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Template Structure</CardTitle>
                  <CardDescription>
                    Define sections and fields for the template
                  </CardDescription>
                </div>
                <Button type="button" onClick={handleAddSection}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Section
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {sections.map((section, sectionIndex) => (
                <Card key={sectionIndex}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Section title"
                          value={section.title}
                          onChange={(e) => handleUpdateSection(sectionIndex, {
                            ...section,
                            title: e.target.value
                          })}
                        />
                        <Textarea
                          placeholder="Section description"
                          value={section.description}
                          onChange={(e) => handleUpdateSection(sectionIndex, {
                            ...section,
                            description: e.target.value
                          })}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSection(sectionIndex)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium">Fields</h4>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddField(sectionIndex)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Field
                        </Button>
                      </div>
                      {section.fields.map((field, fieldIndex) => (
                        <div key={fieldIndex} className="border rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Field name"
                                value={field.name}
                                onChange={(e) => handleUpdateField(sectionIndex, fieldIndex, {
                                  ...field,
                                  name: e.target.value
                                })}
                              />
                              <Input
                                placeholder="Field label"
                                value={field.label}
                                onChange={(e) => handleUpdateField(sectionIndex, fieldIndex, {
                                  ...field,
                                  label: e.target.value
                                })}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteField(sectionIndex, fieldIndex)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Select
                              value={field.type}
                              onValueChange={(value) => handleUpdateField(sectionIndex, fieldIndex, {
                                ...field,
                                type: value
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Field type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Text</SelectItem>
                                <SelectItem value="textarea">Textarea</SelectItem>
                                <SelectItem value="select">Select</SelectItem>
                                <SelectItem value="date">Date</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={field.required}
                                onCheckedChange={(checked) => handleUpdateField(sectionIndex, fieldIndex, {
                                  ...field,
                                  required: checked
                                })}
                              />
                              <span className="text-sm text-muted-foreground">Required</span>
                            </div>
                          </div>
                          <Input
                            placeholder="Placeholder text"
                            value={field.placeholder || ''}
                            onChange={(e) => handleUpdateField(sectionIndex, fieldIndex, {
                              ...field,
                              placeholder: e.target.value
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/cms/notes-templates')}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Template'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AdminNotesTemplateForm;